import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { SubscribeModal } from "../components/SubscribeModal";
import { CampaignCard } from "../components/CampaignCard";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  Search, Bookmark, Compass, Upload, Wand2, Film, ListChecks, Sparkles, Coins, Crown,
} from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, subscription, isPaid } = useAuth();
  const [my, setMy] = useState({ saved: [], recently_viewed: [], imported: [] });
  const [clips, setClips] = useState([]);
  const [modal, setModal] = useState(false);

  useEffect(() => {
    api.get("/my-campaigns").then((r) => setMy(r.data)).catch(() => {});
    api.get("/clips").then((r) => setClips(r.data.clips || [])).catch(() => {});
  }, []);

  const toggleSave = async (c) => {
    try {
      if (c.is_saved) await api.delete(`/campaign/${c.id}/save`);
      else await api.post(`/campaign/${c.id}/save`);
      const r = await api.get("/my-campaigns");
      setMy(r.data);
      toast.success(c.is_saved ? "Removed from saved" : "Campaign saved");
    } catch {
      toast.error("Action failed");
    }
  };

  const gate = (path) => (isPaid ? navigate(path) : setModal(true));

  const freeActions = [
    { icon: Compass, t: "Explore Campaigns", d: "Discover across platforms", to: "/campaigns" },
    { icon: Search, t: "Search Campaigns", d: "Find by keyword or tag", to: "/campaigns" },
    { icon: Bookmark, t: "Saved Campaigns", d: `${my.saved.length} saved`, to: "/my-campaigns" },
  ];
  const paidActions = [
    { icon: Upload, t: "Upload Video", d: "MP4, MOV, WebM", to: "/workspace" },
    { icon: Wand2, t: "Generate Clips", d: "AI clip candidates", to: "/workspace" },
    { icon: Film, t: "My Clips", d: `${clips.length} clips`, to: "/clips" },
    { icon: ListChecks, t: "Processing Jobs", d: "Live progress", to: "/workspace?tab=jobs" },
  ];

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <SubscribeModal open={modal} onOpenChange={setModal} />
      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* header + subscription */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold">
              Welcome back, {user?.name?.split(" ")[0] || "clipper"}
            </h1>
            <p className="text-zinc-500 mt-1">Find campaigns free, then let AI make the clips.</p>
          </div>
          <div
            className="bg-[#141419] border border-white/10 rounded-2xl px-6 py-4 flex items-center gap-4"
            data-testid="subscription-summary"
          >
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isPaid ? "bg-amber-500/15" : "bg-zinc-700/30"}`}>
              {isPaid ? <Crown className="w-5 h-5 text-amber-400" /> : <Sparkles className="w-5 h-5 text-zinc-400" />}
            </div>
            <div>
              <p className="text-xs text-zinc-500 uppercase font-mono tracking-wider">
                {subscription?.plan_config?.name || "Free"} plan
              </p>
              {isPaid ? (
                <p className="font-display font-bold flex items-center gap-1.5">
                  <Coins className="w-4 h-4 text-amber-400" /> {subscription?.credits ?? 0} credits
                </p>
              ) : (
                <button onClick={() => navigate("/pricing")} data-testid="upgrade-inline-btn" className="text-amber-400 hover:text-amber-300 text-sm font-semibold">
                  Unlock AI Clipping →
                </button>
              )}
            </div>
          </div>
        </div>

        {/* FREE */}
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs font-mono uppercase tracking-widest text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-full">Free</span>
            <span className="text-zinc-400 text-sm">Campaign discovery & planning</span>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {freeActions.map((a) => (
              <button
                key={a.t}
                onClick={() => navigate(a.to)}
                data-testid={`dash-free-${a.t.toLowerCase().replace(/\s/g, "-")}`}
                className="bg-[#141419] border border-white/10 rounded-2xl p-6 text-left hover:border-emerald-500/40 hover:-translate-y-1 transition-all group"
              >
                <div className="w-11 h-11 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <a.icon className="w-5 h-5 text-emerald-400" />
                </div>
                <h3 className="font-display font-semibold">{a.t}</h3>
                <p className="text-sm text-zinc-500">{a.d}</p>
              </button>
            ))}
          </div>
        </div>

        {/* PAID */}
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs font-mono uppercase tracking-widest text-amber-400 bg-amber-500/10 border border-amber-500/30 px-2.5 py-1 rounded-full">Paid Pro</span>
            <span className="text-zinc-400 text-sm">AI video processing {!isPaid && "— requires a subscription"}</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {paidActions.map((a) => (
              <button
                key={a.t}
                onClick={() => gate(a.to)}
                data-testid={`dash-paid-${a.t.toLowerCase().replace(/\s/g, "-")}`}
                className={`bg-[#141419] border rounded-2xl p-6 text-left transition-all group relative overflow-hidden ${
                  isPaid ? "border-white/10 hover:border-amber-500/40 hover:-translate-y-1" : "border-white/5 opacity-90 hover:border-amber-500/30"
                }`}
              >
                {!isPaid && (
                  <div className="absolute top-3 right-3 text-amber-400">
                    <Sparkles className="w-4 h-4" />
                  </div>
                )}
                <div className="w-11 h-11 rounded-xl bg-amber-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <a.icon className="w-5 h-5 text-amber-400" />
                </div>
                <h3 className="font-display font-semibold">{a.t}</h3>
                <p className="text-sm text-zinc-500">{isPaid ? a.d : "Subscription required"}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Recent campaigns */}
        {(my.saved.length > 0 || my.recently_viewed.length > 0) && (
          <div>
            <h2 className="font-display text-xl font-bold mb-4">
              {my.saved.length > 0 ? "Saved campaigns" : "Recently viewed"}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(my.saved.length > 0 ? my.saved : my.recently_viewed).slice(0, 6).map((c) => (
                <CampaignCard key={c.id} campaign={c} onToggleSave={toggleSave} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
